/* Stage 73.20 — Full & Final Settlement (F&F) Module with Dynamic Salary Heads,
 * Inline Component Editing, Restore Auto Calculation, Notice Pay Recovery/Payable,
 * Asset Clearance Gate, Validation Banners, and PDF Statement Download.
 */
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import {
  Plus, Download, Calculator, Loader2, CheckCircle2, XCircle, AlertTriangle, X,
  RefreshCw, Save, Eye, Trash2, Copy, Printer, FileText, Check, DollarSign, Search, Filter,
  Edit2, RotateCcw, ChevronDown, ChevronUp, Info, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { EmployeeSelect } from '../components/EmployeeAutocomplete';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const inr = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Standard F&F Master Catalog
const STANDARD_EARNINGS = [
  { code: 'STUB_SALARY', label: 'Salary Till Last Working Day', formula: 'Gross Salary × Worked Days / Total Days in Month' },
  { code: 'LEAVE_ENCASH', label: 'Leave Encashment', formula: 'Encashable Leave Days × (Basic Salary ÷ 30)' },
  { code: 'NOTICE_PAYABLE', label: 'Notice Pay Payable', formula: 'Extra Notice Days Served × (Gross Salary ÷ 30)' },
  { code: 'GRATUITY', label: 'Gratuity', formula: '(Last Drawn Basic × 15 × Years of Service) ÷ 26 (Min 5 years)' },
  { code: 'BONUS', label: 'Statutory / Annual Bonus', formula: 'Annual Statutory Bonus Accrual' },
  { code: 'INCENTIVE', label: 'Incentive / Variable Pay', formula: 'Performance & Sales Variable Payout' },
  { code: 'PERFORMANCE_BONUS', label: 'Performance Bonus', formula: 'Key Performance Indicator (KPI) Bonus' },
  { code: 'EX_GRATIA', label: 'Ex-Gratia', formula: 'Management Discretionary Ex-gratia Payout' },
  { code: 'ARREARS_PENDING', label: 'Pending Arrears', formula: 'Pending Salary Increment & Arrear Revision' },
  { code: 'PENDING_SALARY', label: 'Pending Salary', formula: 'Unpaid Previous Payroll Months Salary' },
  { code: 'REIMBURSEMENTS_PENDING', label: 'Pending Reimbursements', formula: 'Approved Business Expense & Travel Reimbursements' },
  { code: 'PENDING_CLAIMS', label: 'Pending Expense Claims', formula: 'Submitted Expense Claims Awaiting Settlement' },
  { code: 'LTA', label: 'Leave Travel Allowance (LTA)', formula: 'Accrued LTA Exemption & Payout' },
  { code: 'SPECIAL_ALLOWANCE', label: 'Special Allowance', formula: 'Unadjusted Special Allowance Payout' },
  { code: 'OTHER_EARNINGS', label: 'Other Earnings', formula: 'Custom Approved Earning Adjustment' },
  { code: 'EMPLOYER_SETTLEMENT_ADJ', label: 'Employer Settlement Adjustment', formula: 'Company Voluntary Exit Bonus / Loyalty Adjustment' },
];

const STANDARD_DEDUCTIONS = [
  { code: 'PT_BALANCE', label: 'Professional Tax (Remaining Balance)', formula: 'Half-Yearly / Annual Statutory PT Balance True-up' },
  { code: 'PF_EMPLOYEE', label: 'Provident Fund (PF Employee)', formula: 'Prorated Basic × 12% (Subject to ₹15,000 Cap)' },
  { code: 'ESI_EMPLOYEE', label: 'ESI Employee Contribution', formula: 'Prorated Gross × 0.75% (If Gross ≤ ₹21,000)' },
  { code: 'LWF', label: 'Labour Welfare Fund (LWF)', formula: 'State Statutory Employee LWF Contribution' },
  { code: 'TDS', label: 'Income Tax / TDS', formula: 'Annual Projected Income Tax & Settlement TDS True-up' },
  { code: 'LOAN_RECOVERY', label: 'Loan Recovery', formula: 'SUM(Outstanding Principal Balance on Active Loans)' },
  { code: 'ADVANCE_RECOVERY', label: 'Salary Advance Recovery', formula: 'Outstanding Salary Advance Recovery' },
  { code: 'NOTICE_RECOVERY', label: 'Notice Pay Recovery', formula: 'Unserved Short Notice Days × (Gross Salary ÷ 30)' },
  { code: 'ASSET_RECOVERY', label: 'Asset Recovery', formula: 'Recovery Charge for Lost / Damaged / Unreturned Assets' },
  { code: 'SALARY_RECOVERY', label: 'Salary Overpayment Recovery', formula: 'Recovery of Excess Salary Paid in Previous Months' },
  { code: 'LEAVE_RECOVERY', label: 'Unearned Leave Recovery', formula: 'Recovery for Excess Leaves Availed Beyond Accrual' },
  { code: 'EXCESS_LEAVE_RECOVERY', label: 'Excess Leave LOP Recovery', formula: 'LOP Deduction for Unapproved Absences' },
  { code: 'PENDING_DEDUCTIONS', label: 'Pending Deductions', formula: 'Unrecovered Penalties or Operational Deductions' },
  { code: 'PREV_PAYROLL_RECOVERY', label: 'Recovery from Previous Payroll', formula: 'Previous Payroll Adjustment & Retroactive Reversals' },
  { code: 'OTHER_DEDUCTIONS', label: 'Other Deductions', formula: 'Custom Approved Deduction Recovery' },
];

export default function FullAndFinal() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [settlements, setSettlements] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [salaryHeads, setSalaryHeads] = useState([]);

  // Modals & Drawers
  const [showModal, setShowModal] = useState(false);
  const [showAddComponentModal, setShowAddComponentModal] = useState(false);
  const [viewModalDoc, setViewModalDoc] = useState(null);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [componentSearch, setComponentSearch] = useState('');
  const [showAllComponents, setShowAllComponents] = useState(false);

  // Section Collapse States
  const [expandEarnings, setExpandEarnings] = useState(true);
  const [expandDeductions, setExpandDeductions] = useState(true);
  const [expandAssets, setExpandAssets] = useState(true);

  // Auto-compute flow state
  const [computing, setComputing] = useState(false);
  const [checklist, setChecklist] = useState(null);
  const [overrideProrationMethod, setOverrideProrationMethod] = useState(null);
  const [overrideDays, setOverrideDays] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideRemarks, setOverrideRemarks] = useState('');
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [resignationDataError, setResignationDataError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingLineIndex, setEditingLineIndex] = useState(null);
  const [tempLineEdit, setTempLineEdit] = useState({ amount: '', remarks: '' });

  // Add Custom Component Form
  const [newComponentForm, setNewComponentForm] = useState({
    head_code: '', label: '', category: 'earning', amount: '', formula: '', remarks: ''
  });

  const [form, setForm] = useState({
    employee_id: '', separation_date: '', last_working_date: '',
    notice_period_days: 30, notice_days_served: 0, payment_mode: 'Bank Transfer', notes: '',
  });

  // Fetch Base Data
  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  const [pendingEmployees, setPendingEmployees] = useState([]);
  const [selectedPendingEmp, setSelectedPendingEmp] = useState(null);

  const fetchSettlements = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/payroll/fnf?company_id=${selectedCompany}`, { withCredentials: true });
      setSettlements(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  const fetchPendingEmployees = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/payroll/fnf/pending-employees?company_id=${selectedCompany}`, { withCredentials: true });
      setPendingEmployees(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  const fetchEmployees = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/employees?company_id=${selectedCompany}`, { withCredentials: true });
      setEmployees(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  const fetchSalaryHeads = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/masters/salary-heads?company_id=${selectedCompany}`, { withCredentials: true });
      setSalaryHeads(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  useEffect(() => {
    if (selectedCompany) {
      fetchSettlements();
      fetchPendingEmployees();
      fetchEmployees();
      fetchSalaryHeads();
    }
  }, [selectedCompany, fetchSettlements, fetchPendingEmployees, fetchEmployees, fetchSalaryHeads]);

  const handleSelectEmployeeId = async (empId) => {
    setResignationDataError('');
    const pEmp = pendingEmployees.find(e => (e.id === empId || e.employee_id === empId)) || employees.find(e => (e.id === empId || e._id === empId));
    setSelectedPendingEmp(pEmp || null);
    setChecklist(null);
    setOverrideProrationMethod(null);
    setOverrideDays('');
    setOverrideReason('');
    setOverrideRemarks('');

    if (!empId) {
      setForm((prev) => ({ ...prev, employee_id: '' }));
      return;
    }

    try {
      // Step 1 & 2: Fetch approved resignation record directly from API Single Source of Truth
      let resData = null;
      try {
        const { data } = await axios.get(`${API_URL}/hr/resignation/${empId}`, { withCredentials: true });
        resData = data;
      } catch (err) {
        const fallback = await axios.get(`${API_URL}/payroll/fnf/resignation/${empId}`, { withCredentials: true });
        resData = fallback.data;
      }

      if (!resData || resData.success === false) {
        const errMsg = resData?.error || 'Unable to retrieve Resignation details. Please retry or contact the administrator.';
        setResignationDataError(errMsg);
        setForm((prev) => ({
          ...prev,
          employee_id: empId,
          separation_date: '',
          last_working_date: '',
        }));
        return;
      }

      const info = resData.data || {};
      const lwd = info.last_working_day || info.relieving_date || '';
      const sep = info.separation_date || info.relieving_date || lwd || '';

      if (!lwd) {
        setResignationDataError('Last Working Day has not been finalized in the Resignation module.');
        setForm((prev) => ({ ...prev, employee_id: empId, separation_date: '', last_working_date: '' }));
        return;
      }

      setResignationDataError('');
      setForm((prev) => ({
        ...prev,
        employee_id: empId,
        separation_date: sep,
        last_working_date: lwd,
        payroll_month: info.target_settlement_month || (lwd ? lwd.slice(0, 7) : ''),
        notice_period_days: info.notice_days || 30,
        notice_days_served: info.notice_served || 0,
        notes: info.exit_reason ? `Exit Reason: ${info.exit_reason}` : prev.notes,
      }));

    } catch (e) {
      const apiErr = e.response?.data?.error || e.response?.data?.detail || 'Unable to retrieve Resignation details. Please retry or contact the administrator.';
      setResignationDataError(apiErr);
      setForm((prev) => ({ ...prev, employee_id: empId, separation_date: '', last_working_date: '' }));
    }
  };

  const resetForm = () => {
    setForm({
      employee_id: '', separation_date: '', last_working_date: '',
      notice_period_days: 30, notice_days_served: 0, payment_mode: 'Bank Transfer', notes: '',
    });
    setSelectedPendingEmp(null);
    setChecklist(null);
    setOverrideProrationMethod(null);
    setOverrideDays('');
    setOverrideReason('');
    setOverrideRemarks('');
    setResignationDataError('');
    setEditingId(null);
    setEditingLineIndex(null);
    setShowAllComponents(false);
  };

  // Auto Compute Checklist
  const handleCompute = async (overrideMethodArg = null, customDaysArg = null, reasonArg = null, remarksArg = null) => {
    if (!form.employee_id || !form.separation_date || !form.last_working_date) {
      toast.error('Pick employee + separation date + LWD first');
      return;
    }
    if (resignationDataError) {
      toast.error(resignationDataError);
      return;
    }
    setComputing(true);
    const effectiveOverrideMethod = (typeof overrideMethodArg === 'string' && overrideMethodArg)
      ? overrideMethodArg
      : overrideProrationMethod;

    const effectiveOverrideDays = (customDaysArg !== null && customDaysArg !== undefined)
      ? customDaysArg
      : overrideDays;

    const effectiveReason = (reasonArg !== null && reasonArg !== undefined) ? reasonArg : overrideReason;
    const effectiveRemarks = (remarksArg !== null && remarksArg !== undefined) ? remarksArg : overrideRemarks;

    try {
      const { data } = await axios.post(`${API_URL}/payroll/fnf/compute`,
        {
          ...form,
          company_id: selectedCompany,
          override_proration_method: effectiveOverrideMethod,
          salary_proration_method: effectiveOverrideMethod,
          calculation_method: effectiveOverrideMethod,
          override_base_days: effectiveOverrideDays ? Number(effectiveOverrideDays) : null,
          override_reason: effectiveReason,
          override_remarks: effectiveRemarks,
        },
        { withCredentials: true });
      
      setResignationDataError('');
      // Enhance checklist items with original_amount and manual override flags
      const enhancedChecklist = (data.checklist || []).map((item) => ({
        ...item,
        original_amount: Number(item.amount) || 0,
        amount: Number(item.amount) || 0,
        is_manual_override: false,
        remarks: item.remarks || '',
      }));

      // Merge standard missing zero-amount catalog placeholders
      const existingCodes = new Set(enhancedChecklist.map(i => i.code));
      STANDARD_EARNINGS.forEach(se => {
        if (!existingCodes.has(se.code)) {
          enhancedChecklist.push({
            code: se.code, label: se.label, category: 'earning', amount: 0, original_amount: 0,
            editable: true, computed_by: 'engine', formula: se.formula, remarks: '', is_manual_override: false,
          });
        }
      });
      STANDARD_DEDUCTIONS.forEach(sd => {
        if (!existingCodes.has(sd.code)) {
          enhancedChecklist.push({
            code: sd.code, label: sd.label, category: 'deduction', amount: 0, original_amount: 0,
            editable: true, computed_by: 'engine', formula: sd.formula, remarks: '', is_manual_override: false,
          });
        }
      });

      if (data.target_settlement_month || data.payroll_month) {
        setForm(prev => ({ ...prev, payroll_month: data.target_settlement_month || data.payroll_month }));
      }
      setChecklist({ ...data, checklist: enhancedChecklist });
      toast.success('Checklist auto-computed');
    } catch (e) {
      const errMsg = e.response?.data?.detail || 'Compute failed';
      toast.error(errMsg);
      if (errMsg.includes('Relieving details could not be fetched')) {
        setResignationDataError(errMsg);
      }
    }
    setComputing(false);
  };

  // Recalculate totals helper
  const recomputeTotals = (items) => {
    const earn = items.filter(i => i.category === 'earning').reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const ded = items.filter(i => i.category === 'deduction').reduce((s, i) => s + (Number(i.amount) || 0), 0);
    return {
      total_earnings: Math.round(earn * 100) / 100,
      total_deductions: Math.round(ded * 100) / 100,
      net_settlement: Math.round((earn - ded) * 100) / 100,
    };
  };

  // Start Inline Row Edit
  const startRowEdit = (idx, item) => {
    setEditingLineIndex(idx);
    setTempLineEdit({ amount: item.amount, remarks: item.remarks || '' });
  };

  // Save Inline Row Edit
  const saveRowEdit = (idx) => {
    const nextList = [...checklist.checklist];
    const newAmt = parseFloat(tempLineEdit.amount) || 0;
    const origAmt = nextList[idx].original_amount ?? nextList[idx].amount;
    const isOverride = Math.abs(newAmt - origAmt) > 0.001;

    nextList[idx] = {
      ...nextList[idx],
      amount: newAmt,
      remarks: tempLineEdit.remarks,
      is_manual_override: isOverride,
    };

    const newTotals = recomputeTotals(nextList);
    setChecklist({ ...checklist, checklist: nextList, totals: newTotals });
    setEditingLineIndex(null);
    toast.success(`Updated ${nextList[idx].label}`);
  };

  // Cancel Inline Row Edit
  const cancelRowEdit = () => {
    setEditingLineIndex(null);
  };

  // Restore Auto Calculation
  const restoreAutoCalculation = (idx) => {
    const nextList = [...checklist.checklist];
    const origAmt = nextList[idx].original_amount ?? 0;
    nextList[idx] = {
      ...nextList[idx],
      amount: origAmt,
      is_manual_override: false,
    };
    const newTotals = recomputeTotals(nextList);
    setChecklist({ ...checklist, checklist: nextList, totals: newTotals });
    setEditingLineIndex(null);
    toast.info(`Restored auto calculation for ${nextList[idx].label}`);
  };

  // Delete Component
  const deleteComponentRow = (idx) => {
    const nextList = checklist.checklist.filter((_, i) => i !== idx);
    const newTotals = recomputeTotals(nextList);
    setChecklist({ ...checklist, checklist: nextList, totals: newTotals });
    toast.success('Component removed');
  };

  // Add Custom Component
  const handleAddCustomComponent = () => {
    if (!newComponentForm.label || newComponentForm.amount === '') {
      toast.error('Component label and amount are required');
      return;
    }
    const amt = parseFloat(newComponentForm.amount) || 0;
    const rawCode = newComponentForm.head_code || `CUSTOM_${Date.now()}`;
    const code = rawCode.toUpperCase();
    const cat = (newComponentForm.category || 'earning').toLowerCase();

    const nextList = [...(checklist?.checklist || [])];
    const existingIdx = nextList.findIndex(i => (i.code || i.head_code || '').toUpperCase() === code || i.label === newComponentForm.label);

    if (existingIdx >= 0) {
      nextList[existingIdx] = {
        ...nextList[existingIdx],
        code: code,
        head_code: code,
        label: newComponentForm.label,
        head_name: newComponentForm.label,
        category: cat,
        amount: amt,
        remarks: newComponentForm.remarks || nextList[existingIdx].remarks || '',
        is_manual_override: true,
      };
    } else {
      nextList.push({
        code: code,
        head_code: code,
        label: newComponentForm.label,
        head_name: newComponentForm.label,
        category: cat,
        amount: amt,
        original_amount: amt,
        editable: true,
        computed_by: 'manual',
        formula: newComponentForm.formula || 'Custom HR Settlement Addition',
        remarks: newComponentForm.remarks || '',
        is_manual_override: true,
      });
    }

    const newTotals = recomputeTotals(nextList);
    setChecklist({ ...checklist, checklist: nextList, totals: newTotals });

    setShowAddComponentModal(false);
    setNewComponentForm({ head_code: '', label: '', category: 'earning', amount: '', formula: '', remarks: '' });
    toast.success('Custom component added to settlement');
  };

  // Asset Action Toggle
  const handleAssetAction = (assetId, newStatus, recoveryAmt = 0) => {
    if (!checklist?.asset_clearance) return;
    const assets = [...(checklist.asset_clearance.pending_assets || [])];
    const target = assets.find(a => a.id === assetId);
    if (target) {
      target.status = newStatus;
      target.recovery_amount = recoveryAmt;
    }

    // Recalculate total asset recovery
    const totRec = assets.reduce((s, a) => s + (Number(a.recovery_amount) || 0), 0);
    const cleared = assets.every(a => ['returned', 'lost', 'damaged', 'written_off', 'waived'].includes(a.status));

    // Update ASSET_RECOVERY row in checklist
    const nextChecklist = [...checklist.checklist];
    const assetRowIdx = nextChecklist.findIndex(i => i.code === 'ASSET_RECOVERY');
    if (assetRowIdx >= 0) {
      nextChecklist[assetRowIdx] = {
        ...nextChecklist[assetRowIdx],
        amount: round2(totRec),
        formula: `Sum of recovery amounts on lost/damaged assets = ${inr(totRec)}`,
      };
    }

    const newTotals = recomputeTotals(nextChecklist);
    setChecklist({
      ...checklist,
      checklist: nextChecklist,
      totals: newTotals,
      asset_clearance: {
        ...checklist.asset_clearance,
        cleared,
        pending_assets: assets,
        total_recovery: totRec,
      },
    });
  };

  const round2 = (n) => Math.round(n * 100) / 100;

  // Edit Settlement Document (Load existing settlement in editable mode)
  const handleEdit = (settlement) => {
    const sid = settlement.id || settlement._id;
    setEditingId(sid);
    setForm({
      employee_id: settlement.employee_id,
      separation_date: settlement.separation_date || '',
      last_working_date: settlement.last_working_date || settlement.last_working_day || '',
      notice_period_days: settlement.notice_period_days || 30,
      notice_days_served: settlement.notice_days_served || 0,
      payment_mode: settlement.payment_mode || 'Bank Transfer',
      payroll_month: settlement.payroll_month || settlement.settlement_month || '',
      notes: settlement.notes || '',
    });

    const empMatch = pendingEmployees.find(e => e.id === settlement.employee_id || e.employee_id === settlement.employee_id)
      || employees.find(e => e.id === settlement.employee_id || e._id === settlement.employee_id);

    setSelectedPendingEmp(empMatch || {
      id: settlement.employee_id,
      employee_id: settlement.employee_id,
      employee_code: settlement.employee_code,
      employee_name: settlement.employee_name,
      department: settlement.department,
      designation: settlement.designation,
      date_of_joining: settlement.date_of_joining,
      bank_account: settlement.bank_account || '',
      payroll_month: settlement.payroll_month || settlement.settlement_month,
    });

    let itemsList = [];
    if (settlement.settlement_components && Array.isArray(settlement.settlement_components) && settlement.settlement_components.length > 0) {
      itemsList = settlement.settlement_components.map(item => ({
        ...item,
        code: (item.code || item.head_code || item.component_code || 'COMPONENT').toUpperCase(),
        head_code: (item.head_code || item.code || item.component_code || 'COMPONENT').toUpperCase(),
        label: item.label || item.head_name || item.component_name || item.name || 'Component',
        head_name: item.head_name || item.label || item.component_name || item.name || 'Component',
        amount: Number(item.amount) || 0,
        original_amount: item.original_amount !== undefined ? Number(item.original_amount) : Number(item.amount) || 0,
        category: (item.category || item.type || 'earning').toLowerCase(),
        is_manual_override: item.is_manual_override || false,
        remarks: item.remarks || '',
        editable: item.editable !== undefined ? item.editable : true,
        formula: item.formula || '',
      }));
    } else {
      (settlement.earnings_breakdown || []).forEach(e => {
        itemsList.push({
          code: (e.head_code || e.code || 'EARNING').toUpperCase(),
          head_code: (e.head_code || e.code || 'EARNING').toUpperCase(),
          label: e.head_name || e.label || e.name || 'Earning',
          head_name: e.head_name || e.label || e.name || 'Earning',
          category: 'earning',
          amount: Number(e.amount) || 0,
          original_amount: Number(e.amount) || 0,
          editable: true,
          remarks: e.remarks || '',
          is_manual_override: true,
          formula: e.formula || '',
        });
      });
      (settlement.deductions_breakdown || []).forEach(d => {
        itemsList.push({
          code: (d.head_code || d.code || 'DEDUCTION').toUpperCase(),
          head_code: (d.head_code || d.code || 'DEDUCTION').toUpperCase(),
          label: d.head_name || d.label || d.name || 'Deduction',
          head_name: d.head_name || d.label || d.name || 'Deduction',
          category: 'deduction',
          amount: Number(d.amount) || 0,
          original_amount: Number(d.amount) || 0,
          editable: true,
          remarks: d.remarks || '',
          is_manual_override: true,
          formula: d.formula || '',
        });
      });
    }

    const totalEarnings = Number(settlement.total_earnings) || itemsList.filter(i => i.category === 'earning').reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const totalDeductions = Number(settlement.total_deductions) || itemsList.filter(i => i.category === 'deduction').reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const netSettlement = Number(settlement.net_settlement) || (totalEarnings - totalDeductions);

    setChecklist({
      employee_code: settlement.employee_code,
      employee_name: settlement.employee_name,
      work_state: settlement.work_state || '',
      date_of_joining: settlement.date_of_joining || '',
      eligibility: settlement.eligibility || { pt: true, lwf: true, pf: true, esi: true, gratuity: true },
      checklist: itemsList,
      totals: {
        total_earnings: Math.round(totalEarnings * 100) / 100,
        total_deductions: Math.round(totalDeductions * 100) / 100,
        net_settlement: Math.round(netSettlement * 100) / 100,
      },
      engine_version: settlement.engine_version || '1.0',
    });

    setShowModal(true);
  };

  // Save Settlement Document
  const handleSave = async () => {
    if (!checklist) { toast.error('Compute the checklist first'); return; }
    try {
      const formattedComponents = (checklist.checklist || []).map(i => ({
        ...i,
        code: (i.code || i.head_code || 'COMPONENT').toUpperCase(),
        head_code: (i.head_code || i.code || 'COMPONENT').toUpperCase(),
        label: i.label || i.head_name || i.name || 'Component',
        head_name: i.head_name || i.label || i.name || 'Component',
        amount: Number(i.amount) || 0,
      }));

      const earnings = formattedComponents.filter(i => i.category === 'earning' && (i.amount !== 0 || i.is_manual_override));
      const deductions = formattedComponents.filter(i => i.category === 'deduction' && (i.amount !== 0 || i.is_manual_override));
      const find = (code) => Number((formattedComponents.find(i => i.code === code) || {}).amount) || 0;
      
      const pMonth = form.payroll_month || (form.last_working_date ? form.last_working_date.slice(0, 7) : '');

      const payload = {
        employee_id: form.employee_id,
        company_id: selectedCompany,
        employee_code: checklist.employee_code,
        employee_name: checklist.employee_name,
        separation_date: form.separation_date,
        last_working_date: form.last_working_date,
        payment_mode: form.payment_mode,
        leave_encashment_days: (formattedComponents.find(i => i.code === 'LEAVE_ENCASH') || {}).leave_days || 0,
        leave_encashment_amount: find('LEAVE_ENCASH'),
        notice_pay_recovery: find('NOTICE_RECOVERY'),
        notice_pay_payable: find('NOTICE_PAYABLE'),
        gratuity_amount: find('GRATUITY'),
        bonus_amount: find('BONUS'),
        settlement_components: formattedComponents,
        payroll_month: pMonth,
        settlement_month: pMonth,
        total_earnings: checklist.totals.total_earnings,
        total_deductions: checklist.totals.total_deductions,
        net_settlement: checklist.totals.net_settlement,
        gross_settlement: checklist.totals.total_earnings,
        status: editingId ? undefined : 'ready_for_payroll',
        engine_version: checklist.engine_version,
        earnings_breakdown: earnings,
        deductions_breakdown: deductions,
      };

      if (editingId) {
        await axios.patch(`${API_URL}/payroll/fnf/${editingId}`, payload, { withCredentials: true });
        toast.success('Settlement updated successfully');
      } else {
        await axios.post(`${API_URL}/payroll/fnf`, payload, { withCredentials: true });
        toast.success('Settlement saved as Ready for Payroll! Amount will be paid during Payroll Run.');
      }

      setShowModal(false);
      resetForm();
      fetchSettlements();
      fetchPendingEmployees();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleAction = async (fnfId, action, remarks = '') => {
    try {
      await axios.post(`${API_URL}/payroll/fnf/${fnfId}/action`, { action, remarks }, { withCredentials: true });
      toast.success(`Settlement action '${action}' completed`);
      fetchSettlements();
      if (viewModalDoc && (viewModalDoc.id === fnfId || viewModalDoc._id === fnfId)) {
        setViewModalDoc(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const handleDelete = async (fnfId, status) => {
    if (['approved', 'paid'].includes(status)) {
      toast.error('Approved or Paid settlements cannot be deleted');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this settlement record?')) return;
    try {
      await axios.delete(`${API_URL}/payroll/fnf/${fnfId}`, { withCredentials: true });
      toast.success('Settlement deleted');
      fetchSettlements();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleClone = async (settlement) => {
    try {
      setForm({
        employee_id: settlement.employee_id,
        separation_date: settlement.separation_date,
        last_working_date: settlement.last_working_date,
        notice_period_days: 30,
        notice_days_served: 0,
        payment_mode: settlement.payment_mode || 'Bank Transfer',
        notes: `Cloned from ${settlement.employee_code}`,
      });
      setEditingId(null);
      setShowModal(true);
      toast.info('Settlement loaded into editor as new draft');
    } catch (e) {
      toast.error('Failed to clone');
    }
  };

  const handleDownloadPDF = (fnfId) => {
    window.open(`${API_URL}/payroll/fnf/${fnfId}/pdf`, '_blank');
  };

  const handlePrint = (settlement) => {
    window.open(`${API_URL}/payroll/fnf/${settlement.id || settlement._id}/pdf`, '_blank');
  };

  // Dashboard Metrics
  const stats = useMemo(() => {
    const total = settlements.length;
    const pending = settlements.filter(s => s.status === 'pending_approval').length;
    const approved = settlements.filter(s => s.status === 'approved').length;
    const paid = settlements.filter(s => s.status === 'paid').length;
    const cancelled = settlements.filter(s => s.status === 'cancelled').length;
    const paidSum = settlements.filter(s => s.status === 'paid').reduce((s, item) => s + (Number(item.net_settlement) || 0), 0);
    const avgPaid = paid > 0 ? paidSum / paid : 0;
    return { total, pending, approved, paid, cancelled, paidSum, avgPaid };
  }, [settlements]);

  // Filtered Grid
  const filteredSettlements = useMemo(() => {
    return settlements.filter(s => {
      const q = searchQuery.trim().toLowerCase();
      const matchesSearch = !q || (
        (s.employee_code || '').toLowerCase().includes(q) ||
        (s.employee_name || '').toLowerCase().includes(q) ||
        (s.department || '').toLowerCase().includes(q) ||
        (s.designation || '').toLowerCase().includes(q)
      );
      const matchesStatus = statusFilter === 'ALL' || (s.status || '').toLowerCase() === statusFilter.toLowerCase();
      return matchesSearch && matchesStatus;
    });
  }, [settlements, searchQuery, statusFilter]);

  // Filtered Component Checklist in Modal
  const visibleChecklistItems = useMemo(() => {
    if (!checklist?.checklist) return [];
    return checklist.checklist.filter((item) => {
      // Component Search Filter
      const q = componentSearch.trim().toLowerCase();
      if (q && !(item.label.toLowerCase().includes(q) || item.code.toLowerCase().includes(q))) {
        return false;
      }
      // Zero Amount Filter
      if (!showAllComponents && item.amount === 0 && !item.is_manual_override) {
        return false;
      }
      return true;
    });
  }, [checklist, componentSearch, showAllComponents]);

  const eligBadge = (label, eligible) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${eligible ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
      {eligible ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {label}
    </span>
  );

  const statusColors = {
    draft: 'bg-slate-100 text-slate-600 border-slate-200',
    pending_approval: 'bg-amber-100 text-amber-700 border-amber-200',
    approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    paid: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    cancelled: 'bg-rose-100 text-rose-700 border-rose-200',
  };

  return (
    <MainLayout>
      <div data-testid="fnf-page" className="space-y-6 p-6">
        {/* Top Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Calculator className="w-7 h-7 text-[#0052FF]" /> Full &amp; Final Settlement (F&amp;F)
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Automated exit settlement engine with dynamic salary heads, inline row editing, restore auto-calc, and PDF statements.
            </p>
          </div>
          <div className="flex items-center space-x-3 flex-wrap">
            <select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="fnf-company-select">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button data-testid="new-fnf-btn" onClick={() => { resetForm(); setShowModal(true); }}
              className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC] transition-colors shadow-sm">
              <Plus className="w-4 h-4" /><span>New Settlement</span>
            </button>
            <button data-testid="fnf-export-btn" onClick={() => {
              if (settlements.length === 0) { toast.error('No settlements to export'); return; }
              downloadCSV(settlements.map(s => ({
                Employee_Code: s.employee_code || '',
                Employee_Name: s.employee_name || '',
                Department: s.department || '',
                Designation: s.designation || '',
                DOJ: s.date_of_joining || '',
                Separation_Date: s.separation_date || '',
                Last_Working_Day: s.last_working_date || '',
                Payroll_Month: s.payroll_month || '',
                Total_Earnings: s.total_earnings,
                Total_Deductions: s.total_deductions,
                Net_Settlement: s.net_settlement,
                Status: s.status,
              })), 'fnf_settlements.csv');
              toast.success('F&F settlements exported to CSV');
            }} className="flex items-center space-x-2 bg-slate-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors">
              <Download className="w-4 h-4" /><span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* Dashboard Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3" data-testid="fnf-stat-cards">
          <div className="bg-white border border-slate-200 rounded-xl p-3 text-center">
            <p className="text-[10px] font-semibold uppercase text-slate-500">Total</p>
            <p className="text-xl font-bold text-slate-900 mt-1 font-mono">{stats.total}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
            <p className="text-[10px] font-semibold uppercase text-amber-700">Pending</p>
            <p className="text-xl font-bold text-amber-900 mt-1 font-mono">{stats.pending}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
            <p className="text-[10px] font-semibold uppercase text-emerald-700">Approved</p>
            <p className="text-xl font-bold text-emerald-900 mt-1 font-mono">{stats.approved}</p>
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-center">
            <p className="text-[10px] font-semibold uppercase text-indigo-700">Paid</p>
            <p className="text-xl font-bold text-indigo-900 mt-1 font-mono">{stats.paid}</p>
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-center">
            <p className="text-[10px] font-semibold uppercase text-rose-700">Cancelled</p>
            <p className="text-xl font-bold text-rose-900 mt-1 font-mono">{stats.cancelled}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-3 text-center col-span-2">
            <p className="text-[10px] font-semibold uppercase text-slate-500">Total Amount Paid</p>
            <p className="text-xl font-bold text-emerald-600 mt-1 font-mono">{inr(stats.paidSum)}</p>
          </div>
        </div>

        {/* Toolbar & Search Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              placeholder="Search employee code, name, department..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-9 pl-10 pr-3 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none focus:bg-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 px-3 bg-slate-50 border border-slate-200 rounded-md text-sm font-medium text-slate-700 outline-none"
            >
              <option value="ALL">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Main Settlement List Grid */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto shadow-sm">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="px-3 py-2.5 text-left font-medium">Employee</th>
                <th className="px-3 py-2.5 text-left font-medium">Dept / Designation</th>
                <th className="px-3 py-2.5 text-left font-medium">DOJ</th>
                <th className="px-3 py-2.5 text-left font-medium">Separation / LWD</th>
                <th className="px-3 py-2.5 text-right font-medium">Total Earnings</th>
                <th className="px-3 py-2.5 text-right font-medium">Total Deductions</th>
                <th className="px-3 py-2.5 text-right font-medium">Net Settlement</th>
                <th className="px-3 py-2.5 text-center font-medium">Status</th>
                <th className="px-3 py-2.5 text-center font-medium min-w-[180px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredSettlements.map((s) => {
                const sid = s.id || s._id;
                const isDraft = (s.status || 'draft').toLowerCase() === 'draft';
                const isApprovedOrPaid = ['approved', 'paid'].includes((s.status || '').toLowerCase());
                return (
                  <tr key={sid} data-testid={`fnf-row-${sid}`} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-3 py-2.5 font-medium text-slate-900">
                      <div>{s.employee_name || '—'}</div>
                      <div className="text-[10px] text-slate-500 font-mono flex items-center gap-1">
                        <span>{s.employee_code || s.employee_id}</span>
                        {s.settlement_number && <span className="bg-blue-50 text-blue-700 px-1.5 py-0.2 rounded border border-blue-200">{s.settlement_number}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">
                      <div>{s.department || '—'}</div>
                      <div className="text-[10px] text-slate-400">{s.designation || '—'}</div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">{s.date_of_joining || '—'}</td>
                    <td className="px-3 py-2.5 text-slate-600">
                      <div>{s.separation_date || '—'}</div>
                      <div className="text-[10px] text-slate-400">LWD: {s.last_working_date || '—'}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-600 font-semibold">{inr(s.total_earnings)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-rose-600 font-semibold">{inr(s.total_deductions)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-900">{inr(s.net_settlement)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusColors[s.status] || 'bg-slate-100 text-slate-600'}`}>
                        {(s.status || 'draft').replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => setViewModalDoc(s)} title="View Settlement Detail" className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded">
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        {s.status !== 'paid' && s.status !== 'Paid' && (
                          <button onClick={() => handleEdit(s)} title="Edit Settlement" data-testid={`fnf-edit-btn-${sid}`} className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded">
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => handleDownloadPDF(sid)} title="Download PDF Statement" className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded">
                          <FileText className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handlePrint(s)} title="Print PDF" className="p-1 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded">
                          <Printer className="w-3.5 h-3.5" />
                        </button>
                        {isDraft && (
                          <button onClick={() => handleAction(sid, 'approve')} title="Approve Settlement" className="p-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded">
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {s.status === 'approved' && (
                          <button onClick={() => handleAction(sid, 'pay')} title="Mark as Paid" className="p-1 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded">
                            <DollarSign className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => handleClone(s)} title="Clone Settlement" className="p-1 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(sid, s.status)}
                          disabled={isApprovedOrPaid}
                          title={isApprovedOrPaid ? 'Approved or Paid settlements cannot be deleted' : 'Delete Settlement'}
                          className={`p-1 rounded ${isApprovedOrPaid ? 'text-slate-300 cursor-not-allowed' : 'text-rose-600 hover:text-rose-800 hover:bg-rose-50'}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredSettlements.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400 text-sm">No settlement records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* View Settlement Detail Modal */}
        {viewModalDoc && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewModalDoc(null)}>
            <div className="bg-white rounded-xl w-full max-w-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-50">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">
                    F&amp;F Statement — {viewModalDoc.employee_name} ({viewModalDoc.employee_code})
                  </h2>
                  <p className="text-xs text-slate-500">Separation Date: {viewModalDoc.separation_date} · LWD: {viewModalDoc.last_working_date}</p>
                </div>
                <button onClick={() => setViewModalDoc(null)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto">
                <div className="grid grid-cols-3 gap-3 text-xs bg-slate-50 p-3 rounded-lg border border-slate-200">
                  <div><span className="text-slate-500">Department:</span> <b>{viewModalDoc.department || '—'}</b></div>
                  <div><span className="text-slate-500">Designation:</span> <b>{viewModalDoc.designation || '—'}</b></div>
                  <div><span className="text-slate-500">Status:</span> <b className="uppercase">{viewModalDoc.status}</b></div>
                </div>

                {/* Totals Summary */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <p className="text-xs text-emerald-700 font-medium">Total Earnings</p>
                    <p className="text-xl font-bold text-emerald-900 font-mono mt-1">{inr(viewModalDoc.total_earnings)}</p>
                  </div>
                  <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
                    <p className="text-xs text-rose-700 font-medium">Total Deductions</p>
                    <p className="text-xl font-bold text-rose-900 font-mono mt-1">{inr(viewModalDoc.total_deductions)}</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-700 font-medium">Net Settlement</p>
                    <p className="text-xl font-bold text-blue-900 font-mono mt-1">{inr(viewModalDoc.net_settlement)}</p>
                  </div>
                </div>

                {/* Component Table */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600 font-semibold border-b">
                      <tr>
                        <th className="px-3 py-2 text-left">Component</th>
                        <th className="px-3 py-2 text-left">Formula / Audit Note</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(viewModalDoc.settlement_components || viewModalDoc.checklist || []).map((item, idx) => (
                        <tr key={idx} className={item.category === 'earning' ? 'bg-emerald-50/30' : 'bg-rose-50/30'}>
                          <td className="px-3 py-2 font-medium text-slate-800">
                            {item.label}
                            {item.is_manual_override && (
                              <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-100 text-amber-800">
                                Manual Override
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-500 italic text-[11px]">{item.formula}</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${item.category === 'earning' ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {item.category === 'earning' ? '+' : '−'} {inr(item.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex items-center justify-between px-6 py-3 border-t bg-slate-50">
                <button
                  onClick={() => handleDownloadPDF(viewModalDoc.id || viewModalDoc._id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-semibold"
                >
                  <FileText className="w-4 h-4" /> Download PDF Statement
                </button>
                <div className="flex items-center gap-2">
                  {viewModalDoc.status !== 'paid' && viewModalDoc.status !== 'Paid' && (
                    <button
                      onClick={() => {
                        const doc = viewModalDoc;
                        setViewModalDoc(null);
                        handleEdit(doc);
                      }}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 border border-blue-200 hover:bg-blue-100 text-blue-700 rounded-md text-xs font-semibold"
                    >
                      <Edit2 className="w-3.5 h-3.5" /> Edit Settlement
                    </button>
                  )}
                  {viewModalDoc.status === 'draft' && (
                    <button onClick={() => handleAction(viewModalDoc.id || viewModalDoc._id, 'approve')} className="px-3 py-1.5 bg-emerald-600 text-white rounded-md text-xs font-semibold">
                      Approve
                    </button>
                  )}
                  {viewModalDoc.status === 'approved' && (
                    <button onClick={() => handleAction(viewModalDoc.id || viewModalDoc._id, 'pay')} className="px-3 py-1.5 bg-indigo-600 text-white rounded-md text-xs font-semibold">
                      Mark as Paid
                    </button>
                  )}
                  <button onClick={() => setViewModalDoc(null)} className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded-md text-xs font-medium">
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create / Edit Settlement Modal (Interactive Auto Compute + Editable Checklist) */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)} data-testid="fnf-modal">
            <div className="bg-white rounded-xl w-full max-w-5xl shadow-2xl flex flex-col max-h-[94vh]" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-3 border-b bg-slate-50 rounded-t-xl flex-shrink-0">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">
                    {editingId ? 'Edit F&F Settlement' : 'New F&F Exit Settlement'}
                  </h2>
                  <p className="text-xs text-slate-500">Pick employee + exit dates → Compute → Edit/Override → Save</p>
                </div>
                <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
                {/* Step 1: Inputs */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3 flex items-center gap-2">
                    <span className="bg-[#0052FF] text-white w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold">1</span>
                    Employee &amp; Exit Parameters
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-3">
                      <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center justify-between">
                        <span>Select Resigned / Exit Eligible Employee</span>
                        {pendingEmployees.length > 0 && (
                          <span className="text-[11px] font-normal text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                            {pendingEmployees.length} Approved Resignation(s) Pending F&F
                          </span>
                        )}
                      </label>
                      {pendingEmployees.length > 0 ? (
                        <select
                          value={form.employee_id}
                          onChange={(e) => handleSelectEmployeeId(e.target.value)}
                          className="w-full border border-blue-300 rounded-lg px-3 py-2 text-sm bg-white font-medium text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                          data-testid="fnf-pending-employee-select"
                        >
                          <option value="">-- Choose Approved Resigned Employee --</option>
                          {pendingEmployees.map((pe) => (
                            <option key={pe.id || pe.employee_id} value={pe.id || pe.employee_id}>
                              [{pe.employee_code}] {pe.employee_name} ({pe.department || 'General'}) - LWD: {pe.last_working_date || pe.resignation_date || 'N/A'}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <EmployeeSelect
                          companyId={selectedCompany}
                          value={form.employee_id}
                          onChange={(empId) => handleSelectEmployeeId(empId)}
                          placeholder="Search Employee by Code or Name…"
                          data-testid="fnf-employee-select"
                        />
                      )}
                    </div>

                    {selectedPendingEmp && (
                      <div className="md:col-span-3 bg-blue-50/70 border border-blue-200 rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div>
                          <span className="text-slate-500 block">Department & Designation:</span>
                          <span className="font-semibold text-slate-800">{selectedPendingEmp.department || '—'} / {selectedPendingEmp.designation || '—'}</span>
                          {selectedPendingEmp.cost_center && <span className="text-[10px] text-slate-500 block">CC: {selectedPendingEmp.cost_center}</span>}
                        </div>
                        <div>
                          <span className="text-slate-500 block">Date of Joining & Cycle:</span>
                          <span className="font-semibold text-slate-800">{selectedPendingEmp.date_of_joining || '—'}</span>
                          {checklist?.salary_proration_method === 'BASE_DAYS' ? (
                            <span className="text-[10px] font-bold text-purple-700 block mt-0.5">
                              Mode: Base Days ({checklist?.proration_base_days || 30} Base Days)
                            </span>
                          ) : (
                            <>
                              {selectedPendingEmp.wage_period_label && <span className="text-[10px] text-blue-600 block">{selectedPendingEmp.wage_period_label}</span>}
                              <span className="text-[10px] font-bold text-blue-700 block mt-0.5">
                                Mode: Wage Days ({checklist?.proration_base_days || 31} Wage Days)
                              </span>
                            </>
                          )}
                        </div>
                        <div>
                          <span className="text-slate-500 block">Target Settlement Month & Formula:</span>
                          <span className="font-semibold text-blue-700">{checklist?.target_settlement_month || checklist?.payroll_month || selectedPendingEmp.payroll_month || form.payroll_month || '—'}</span>
                          {checklist?.formula_summary && <span className="text-[10px] text-slate-600 block font-mono mt-0.5">{checklist.formula_summary}</span>}
                        </div>
                        <div>
                          <span className="text-slate-500 block">Bank Account:</span>
                          <span className="font-semibold text-slate-800">{selectedPendingEmp.bank_account ? `${selectedPendingEmp.bank_account} (${selectedPendingEmp.bank_ifsc || 'IFSC'})` : 'Not Configured'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block">Encashable Leave Balance:</span>
                          <span className="font-semibold text-emerald-700">{selectedPendingEmp.pending_leave_balance ?? 0} Days</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block">Active Loan Balance:</span>
                          <span className="font-semibold text-rose-700">₹ {(selectedPendingEmp.loan_balance ?? 0).toLocaleString('en-IN')}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block">Salary Advance Balance:</span>
                          <span className="font-semibold text-amber-700">₹ {(selectedPendingEmp.advance_balance ?? 0).toLocaleString('en-IN')}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 block">Unreturned Assets:</span>
                          <span className="font-semibold text-purple-700">{selectedPendingEmp.unreturned_assets_count ?? 0} Items Pending</span>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1 flex items-center justify-between">
                        <span>Separation Date</span>
                        {form.separation_date && <span className="text-[10px] text-blue-700 font-semibold bg-blue-50 px-1.5 py-0.5 rounded">Source: Approved Resignation</span>}
                      </label>
                      <input type="date" data-testid="fnf-sep-date" value={form.separation_date}
                        readOnly={true}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-100 text-slate-700 font-medium cursor-not-allowed" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1 flex items-center justify-between">
                        <span>Last Working Day (LWD)</span>
                        {form.last_working_date && <span className="text-[10px] text-blue-700 font-semibold bg-blue-50 px-1.5 py-0.5 rounded">Source: Approved Resignation</span>}
                      </label>
                      <input type="date" data-testid="fnf-lwd" value={form.last_working_date}
                        readOnly={true}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-100 text-slate-700 font-medium cursor-not-allowed" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Notice Period (days)</label>
                      <input type="number" value={form.notice_period_days} readOnly={true}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-100 text-slate-700 font-medium cursor-not-allowed" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Notice Days Served</label>
                      <input type="number" value={form.notice_days_served} readOnly={true}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-100 text-slate-700 font-medium cursor-not-allowed" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Payment Mode</label>
                      <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                        <option value="Bank Transfer">Bank Transfer (NEFT/RTGS)</option>
                        <option value="Cheque">Cheque</option>
                        <option value="Cash">Cash</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button onClick={() => handleCompute()} disabled={computing || Boolean(resignationDataError) || !form.last_working_date}
                      className="flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                      data-testid="fnf-compute-btn">
                      {computing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
                      {checklist ? 'Recompute Auto Calculation' : 'Compute Checklist'}
                    </button>
                  </div>
                </div>

                {/* Step 2: Auto-Computed Component Checklist */}
                {checklist && (
                  <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4" data-testid="fnf-checklist">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                          <span className="bg-emerald-600 text-white w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold">2</span>
                          Settlement Components — {checklist.employee_name} ({checklist.employee_code})
                        </h3>
                        <p className="text-[11px] text-slate-500 ml-7">
                          Work State: <b>{checklist.work_state || '—'}</b> {checklist.date_of_joining ? ` · DOJ: ${checklist.date_of_joining}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowAddComponentModal(true)}
                          className="flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-800 px-2.5 py-1.5 rounded-md font-semibold transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5 text-blue-600" /> Add Custom Component
                        </button>
                        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer bg-slate-50 px-2.5 py-1.5 rounded-md border border-slate-200">
                          <input
                            type="checkbox"
                            checked={showAllComponents}
                            onChange={(e) => setShowAllComponents(e.target.checked)}
                            className="rounded text-blue-600"
                          />
                          Show all standard components (incl. ₹0)
                        </label>
                      </div>
                    </div>

                    {/* Eligibility Badges */}
                    <div className="flex flex-wrap items-center gap-1.5 ml-7" data-testid="fnf-eligibility">
                      {eligBadge('PT', checklist.eligibility.pt)}
                      {eligBadge('LWF', checklist.eligibility.lwf)}
                      {eligBadge('PF', checklist.eligibility.pf)}
                      {eligBadge('ESI', checklist.eligibility.esi)}
                      {eligBadge('Gratuity', checklist.eligibility.gratuity)}
                    </div>

                    {/* Salary Calculation Method Bar & Manual Override (Admin) */}
                    {/* Requirement D: Resignation Link Validation Blocking Prompt Banner */}
                    {resignationDataError && (
                      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r shadow-sm flex items-start gap-3 my-2" data-testid="resignation-blocking-alert">
                        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-900 font-medium leading-relaxed">
                          {resignationDataError}
                        </div>
                      </div>
                    )}

                    {/* Salary Calculation Method Bar & Manual Override (Admin) */}
                    {(() => {
                      const effectiveMethod = overrideProrationMethod || checklist?.salary_proration_method || checklist?.calculation_method || selectedPendingEmp?.salary_proration_method || 'WAGE_DAYS';
                      const isBaseDaysMode = effectiveMethod === 'BASE_DAYS';
                      const prorationLabel = isBaseDaysMode ? 'Base Days' : 'Wage Days';
                      const autoDays = checklist?.original_base_days || (isBaseDaysMode ? 30 : 31);
                      const baseDaysVal = checklist?.override_base_days || checklist?.proration_base_days || checklist?.base_days || autoDays;
                      const hasAdminOverride = checklist?.is_manual_override || Boolean(checklist?.override_base_days);

                      return (
                        <>
                          <div className="bg-indigo-50/80 border border-indigo-200 rounded-lg p-3 space-y-3 text-xs">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-indigo-950">Calculation Mode:</span>
                                <span className="bg-white border border-indigo-300 rounded px-2.5 py-1 font-semibold text-indigo-800 shadow-sm">
                                  Group: {checklist?.ctc_group_name || selectedPendingEmp?.payroll_group || 'STAFF'}
                                </span>
                                <span className={`px-2.5 py-1 rounded font-bold border ${isBaseDaysMode ? 'bg-purple-100 text-purple-900 border-purple-300' : 'bg-blue-100 text-blue-900 border-blue-300'}`}>
                                  Mode: {prorationLabel}
                                </span>
                                {isBaseDaysMode ? (
                                  <>
                                    <span className="bg-white border border-indigo-200 rounded px-2 py-1 text-slate-700 font-medium">
                                      Settlement Month: <b>{checklist?.target_settlement_month || checklist?.payroll_month || 'Apr 2026'}</b>
                                    </span>
                                    <span className="bg-purple-100/70 border border-purple-300 rounded px-2 py-1 font-mono font-bold text-purple-950">
                                      Configured Base Days: {baseDaysVal}
                                    </span>
                                    <span className="text-[11px] text-purple-800 font-semibold self-center bg-purple-50 border border-purple-200 rounded px-2 py-0.5">
                                      Source: {checklist?.base_days_source || 'Payroll Monthly Run'}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    {checklist?.wage_period_config && (
                                      <span className="bg-white border border-blue-200 rounded px-2 py-1 text-slate-700 font-medium">
                                        Cycle: <b>{checklist.wage_period_config.label}</b>
                                      </span>
                                    )}
                                    <span className="bg-blue-100/70 border border-blue-300 rounded px-2 py-1 font-mono font-bold text-blue-950">
                                      Configured Wage Days: {baseDaysVal}
                                    </span>
                                    <span className="text-[11px] text-blue-800 font-semibold self-center bg-blue-50 border border-blue-200 rounded px-2 py-0.5">
                                      Source: {checklist?.base_days_source || 'Payroll Monthly Run'}
                                    </span>
                                  </>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setShowOverrideModal(!showOverrideModal)}
                                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-3 py-1.5 rounded text-xs shadow-sm transition-colors flex items-center gap-1.5"
                                  data-testid="admin-manual-override-toggle-btn"
                                >
                                  Admin Manual Override
                                </button>
                              </div>
                            </div>

                            {/* Side-by-side Auto vs Admin Override Badges */}
                            {hasAdminOverride && (
                              <div className="bg-amber-50 border border-amber-300 rounded p-2 text-xs flex flex-wrap items-center justify-between gap-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-bold text-amber-950">Override Active:</span>
                                  <span className="bg-white border border-slate-300 rounded px-2 py-0.5 text-slate-700 font-medium line-through">
                                    Auto: {autoDays} {prorationLabel}
                                  </span>
                                  <span className="bg-amber-600 text-white font-bold px-2 py-0.5 rounded">
                                    Admin Override: {baseDaysVal} {prorationLabel}
                                  </span>
                                  <span className="text-amber-900 font-semibold">
                                    Reason: <b>{checklist?.override_reason || overrideReason || 'Special Adjustment'}</b>
                                  </span>
                                </div>
                                <div className="text-[11px] text-amber-800 italic">
                                  {checklist?.override_remarks || overrideRemarks} (By {checklist?.override_by || 'Admin'})
                                </div>
                              </div>
                            )}

                            {/* Admin Manual Override Form Drawer */}
                            {showOverrideModal && (
                              <div className="bg-white border border-indigo-300 rounded-lg p-3 mt-2 space-y-3 shadow-inner">
                                <div className="flex items-center justify-between">
                                  <h4 className="font-bold text-indigo-950 text-xs flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-indigo-600"></span>
                                    Admin Manual Override Panel (Requires Mandatory Reason & Remarks)
                                  </h4>
                                  <button
                                    type="button"
                                    onClick={() => setShowOverrideModal(false)}
                                    className="text-slate-400 hover:text-slate-600 text-xs font-bold"
                                  >
                                    ✕ Close
                                  </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  <div>
                                    <label className="block text-[11px] font-semibold text-slate-700 mb-1">Calculation Method</label>
                                    <select
                                      value={effectiveMethod}
                                      onChange={(e) => setOverrideProrationMethod(e.target.value)}
                                      className="w-full bg-slate-50 border border-slate-300 rounded px-2.5 py-1.5 text-xs font-medium text-slate-800"
                                      data-testid="override-method-select"
                                    >
                                      <option value="WAGE_DAYS">Wage Days (Configured Wage Cycle)</option>
                                      <option value="BASE_DAYS">Base Days (Calendar Month Days)</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-[11px] font-semibold text-slate-700 mb-1">Configured Days Override</label>
                                    <input
                                      type="number"
                                      placeholder={`Auto Value: ${autoDays}`}
                                      value={overrideDays}
                                      onChange={(e) => setOverrideDays(e.target.value)}
                                      className="w-full bg-slate-50 border border-slate-300 rounded px-2.5 py-1.5 text-xs font-medium text-slate-800"
                                      data-testid="override-days-input"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[11px] font-semibold text-slate-700 mb-1">Override Reason <span className="text-rose-600">*</span></label>
                                    <select
                                      value={overrideReason}
                                      onChange={(e) => setOverrideReason(e.target.value)}
                                      className="w-full bg-slate-50 border border-slate-300 rounded px-2.5 py-1.5 text-xs font-medium text-slate-800"
                                      data-testid="override-reason-select"
                                    >
                                      <option value="">-- Select Mandatory Reason --</option>
                                      <option value="Factory Shutdown">Factory Shutdown</option>
                                      <option value="Management Approved Adjustment">Management Approved Adjustment</option>
                                      <option value="Special Payroll Policy">Special Payroll Policy</option>
                                      <option value="Contract Agreement Correction">Contract Agreement Correction</option>
                                      <option value="Other">Other</option>
                                    </select>
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-[11px] font-semibold text-slate-700 mb-1">Audit Remarks <span className="text-rose-600">*</span></label>
                                  <textarea
                                    rows={2}
                                    placeholder="Enter detailed justification for management and statutory audit logs..."
                                    value={overrideRemarks}
                                    onChange={(e) => setOverrideRemarks(e.target.value)}
                                    className="w-full bg-slate-50 border border-slate-300 rounded px-2.5 py-1.5 text-xs font-medium text-slate-800"
                                    data-testid="override-remarks-input"
                                  />
                                </div>

                                <div className="flex justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOverrideDays('');
                                      setOverrideReason('');
                                      setOverrideRemarks('');
                                      setOverrideProrationMethod(null);
                                      handleCompute(null, null, '', '');
                                    }}
                                    className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded"
                                  >
                                    Reset to Auto
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (!overrideReason || !overrideRemarks) {
                                        toast.error('Override Reason and Remarks are mandatory before applying manual override.');
                                        return;
                                      }
                                      handleCompute(effectiveMethod, overrideDays, overrideReason, overrideRemarks);
                                      setShowOverrideModal(false);
                                    }}
                                    className="px-4 py-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded shadow-sm"
                                    data-testid="apply-override-btn"
                                  >
                                    Apply & Save Override
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Breakdown Card: Base Days vs Wage Days */}
                          {isBaseDaysMode ? (
                            <div className="bg-purple-50/60 border border-purple-200 rounded-lg p-3 space-y-2">
                              <div className="flex items-center justify-between text-xs">
                                <span className="font-bold text-purple-950 flex items-center gap-1.5">
                                  <span className="w-2 h-2 rounded-full bg-purple-600"></span>
                                  Settlement Month: <span className="text-purple-700 font-bold">{checklist?.target_settlement_month || checklist?.payroll_month || 'Apr 2026'}</span>
                                </span>
                                <span className="text-purple-800 font-bold">Calculation Mode: Base Days (Configured Base Days = {baseDaysVal} · Source: {checklist?.base_days_source || 'Payroll Monthly Run'})</span>
                              </div>
                              {checklist?.wage_periods_breakdown && checklist.wage_periods_breakdown.length > 0 && (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-left text-xs divide-y divide-slate-200 bg-white border rounded-md">
                                    <thead className="bg-purple-100/60 font-semibold text-purple-950">
                                      <tr>
                                        <th className="p-2">Settlement Period</th>
                                        <th className="p-2">Effective Date Window</th>
                                        <th className="p-2 text-center">Configured Base Days</th>
                                        <th className="p-2 text-center">Served / LOP</th>
                                        <th className="p-2 text-center">Paid Days</th>
                                        <th className="p-2 text-right">Prorated Salary</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      {checklist.wage_periods_breakdown.map((wp, wIdx) => (
                                        <tr key={wIdx} className="hover:bg-purple-50/30">
                                          <td className="p-2 font-semibold text-slate-800">{wp.period_name}</td>
                                          <td className="p-2 text-slate-600">{wp.effective_start} to {wp.effective_end}</td>
                                          <td className="p-2 text-center font-mono font-bold text-purple-700">{wp.proration_base_days || baseDaysVal} Days</td>
                                          <td className="p-2 text-center">{wp.served_days} served / <span className="text-rose-600 font-medium">{wp.lop_days} LOP</span></td>
                                          <td className="p-2 text-center font-bold text-emerald-700">{wp.paid_days} Days</td>
                                          <td className="p-2 text-right font-bold text-slate-900">₹ {(wp.gross_salary || 0).toLocaleString('en-IN')}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          ) : (
                            checklist?.wage_period_config && (
                              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                                <div className="flex items-center justify-between text-xs">
                                  <span className="font-bold text-slate-800 flex items-center gap-1.5">
                                    <span className="w-2 h-2 rounded-full bg-blue-600"></span>
                                    Company Wage Cycle: <span className="text-blue-700 font-semibold">{checklist.wage_period_config.label}</span>
                                  </span>
                                  <span className="text-slate-500">Source: {checklist?.base_days_source || 'Payroll Monthly Run'}</span>
                                </div>
                                {checklist.wage_periods_breakdown && checklist.wage_periods_breakdown.length > 0 && (
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-left text-xs divide-y divide-slate-200 bg-white border rounded-md">
                                      <thead className="bg-slate-100 font-semibold text-slate-700">
                                        <tr>
                                          <th className="p-2">Wage Period</th>
                                          <th className="p-2">Effective Date Window</th>
                                          <th className="p-2 text-center">Configured Wage Days</th>
                                          <th className="p-2 text-center">Served / LOP</th>
                                          <th className="p-2 text-center">Paid Days</th>
                                          <th className="p-2 text-right">Prorated Salary</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {checklist.wage_periods_breakdown.map((wp, wIdx) => (
                                          <tr key={wIdx} className="hover:bg-slate-50">
                                            <td className="p-2 font-semibold text-slate-800">{wp.period_name}</td>
                                            <td className="p-2 text-slate-600">{wp.effective_start} to {wp.effective_end}</td>
                                            <td className="p-2 text-center font-mono font-bold text-blue-700">{wp.total_wage_days || baseDaysVal} Wage Days</td>
                                            <td className="p-2 text-center">{wp.served_days} served / <span className="text-rose-600 font-medium">{wp.lop_days} LOP</span></td>
                                            <td className="p-2 text-center font-bold text-emerald-700">{wp.paid_days} Days</td>
                                            <td className="p-2 text-right font-bold text-slate-900">₹ {(wp.gross_salary || 0).toLocaleString('en-IN')}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </>
                      );
                    })()}

                    {/* Component Search Toolbar */}
                    <div className="relative max-w-sm">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                      <input
                        placeholder="Search component name..."
                        value={componentSearch}
                        onChange={(e) => setComponentSearch(e.target.value)}
                        className="w-full h-8 pl-8 pr-3 bg-slate-50 border border-slate-200 rounded text-xs outline-none focus:bg-white"
                      />
                    </div>

                    {/* Component List Table */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-700 font-semibold border-b">
                          <tr>
                            <th className="px-3 py-2 text-left w-[36%]">Component</th>
                            <th className="px-3 py-2 text-left">Formula / Audit Note</th>
                            <th className="px-3 py-2 text-right w-36">Amount (₹)</th>
                            <th className="px-3 py-2 text-center w-20">Type</th>
                            <th className="px-3 py-2 text-center w-28">Row Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {visibleChecklistItems.map((item, idx) => {
                            const originalIdx = checklist.checklist.findIndex(c => c.code === item.code && c.label === item.label);
                            const isEditing = editingLineIndex === originalIdx;

                            return (
                              <tr key={idx} data-testid={`fnf-line-${item.code}`} className={item.category === 'earning' ? 'bg-emerald-50/30' : 'bg-rose-50/30'}>
                                <td className="px-3 py-2 text-slate-900 font-medium">
                                  <div className="flex items-center gap-1.5">
                                    <span>{item.label}</span>
                                    {item.is_manual_override && (
                                      <span className="px-1.5 py-0.2 text-[9px] font-bold rounded bg-amber-100 text-amber-800 border border-amber-300">
                                        Manual Override
                                      </span>
                                    )}
                                  </div>
                                  {item.remarks && (
                                    <div className="text-[10px] text-slate-500 italic mt-0.5">
                                      Remark: {item.remarks}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-slate-500 text-[11px] italic">
                                  {item.formula}
                                  {item.is_manual_override && (
                                    <div className="text-[10px] text-amber-700 not-italic font-mono mt-0.5">
                                      Auto-calc value was: {inr(item.original_amount)}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  {isEditing ? (
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={tempLineEdit.amount}
                                      onChange={(e) => setTempLineEdit({ ...tempLineEdit, amount: e.target.value })}
                                      className="w-28 text-right font-mono border border-blue-400 bg-white rounded px-2 py-1 outline-none font-bold"
                                    />
                                  ) : (
                                    <span className={`font-mono font-bold ${item.category === 'earning' ? 'text-emerald-700' : 'text-rose-700'}`}>
                                      {inr(item.amount)}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${
                                    item.category === 'earning' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                    {item.category === 'earning' ? '+ Earning' : '− Deduction'}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-center">
                                  {isEditing ? (
                                    <div className="flex items-center justify-center gap-1">
                                      <button onClick={() => saveRowEdit(originalIdx)} title="Save Line Edit" className="p-1 text-emerald-700 hover:bg-emerald-100 rounded">
                                        <Check className="w-3.5 h-3.5" />
                                      </button>
                                      <button onClick={cancelRowEdit} title="Cancel Edit" className="p-1 text-slate-500 hover:bg-slate-100 rounded">
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-center gap-1">
                                      <button onClick={() => startRowEdit(originalIdx, item)} title="Edit Amount & Remark" className="p-1 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded">
                                        <Edit2 className="w-3.5 h-3.5" />
                                      </button>
                                      {item.is_manual_override && (
                                        <button onClick={() => restoreAutoCalculation(originalIdx)} title="Restore Auto Calculation" className="p-1 text-amber-600 hover:bg-amber-50 rounded">
                                          <RotateCcw className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                      <button onClick={() => deleteComponentRow(originalIdx)} title="Remove Line" className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded">
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Asset Clearance Section */}
                    {checklist.asset_clearance && (
                      <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                            📦 Asset Recovery &amp; Clearance
                          </h4>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            checklist.asset_clearance.cleared ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                          }`}>
                            {checklist.asset_clearance.cleared ? '✓ Cleared' : `⚠ ${checklist.asset_clearance.pending_count} Assets Pending`}
                          </span>
                        </div>

                        {checklist.asset_clearance.pending_assets?.length > 0 ? (
                          <div className="border border-slate-200 rounded bg-white overflow-hidden text-xs">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-100 text-slate-600">
                                <tr>
                                  <th className="px-2 py-1 text-left">Asset</th>
                                  <th className="px-2 py-1 text-left">Serial / Code</th>
                                  <th className="px-2 py-1 text-center">Status</th>
                                  <th className="px-2 py-1 text-right">Recovery Charge (₹)</th>
                                  <th className="px-2 py-1 text-center">Action</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {checklist.asset_clearance.pending_assets.map((asset) => (
                                  <tr key={asset.id}>
                                    <td className="px-2 py-1 font-medium">{asset.asset_name} ({asset.asset_type})</td>
                                    <td className="px-2 py-1 font-mono text-slate-500">{asset.serial_number || '—'}</td>
                                    <td className="px-2 py-1 text-center capitalize font-semibold">{asset.status}</td>
                                    <td className="px-2 py-1 text-right font-mono font-bold text-rose-600">
                                      {inr(asset.recovery_amount || 0)}
                                    </td>
                                    <td className="px-2 py-1 text-center">
                                      <select
                                        value={asset.status}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          const amt = val === 'damaged' || val === 'lost' ? (asset.recovery_amount || 500) : 0;
                                          handleAssetAction(asset.id, val, amt);
                                        }}
                                        className="text-[11px] border border-slate-300 rounded px-1 py-0.5 bg-white"
                                      >
                                        <option value="issued">Issued (Pending)</option>
                                        <option value="returned">Returned (Cleared)</option>
                                        <option value="lost">Lost (Recover)</option>
                                        <option value="damaged">Damaged (Recover)</option>
                                        <option value="waived">Waived</option>
                                      </select>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-xs text-emerald-700 font-medium">✓ No pending company assets assigned.</p>
                        )}
                      </div>
                    )}

                    {/* Negative Net Warning Alert */}
                    {checklist.totals.net_settlement < 0 && (
                      <div className="bg-amber-50 border border-amber-300 text-amber-900 p-3 rounded-lg flex items-center gap-2 text-xs">
                        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        <div>
                          <b>Net Settlement is Negative ({inr(checklist.totals.net_settlement)}):</b> Deductions exceed total earnings. Employee owes the company money.
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sticky Summary Cards Footer */}
              {checklist && (
                <div className="border-t bg-slate-50 p-4 rounded-b-xl flex-shrink-0 space-y-3">
                  <div className="grid grid-cols-4 gap-3 text-xs" data-testid="fnf-totals">
                    <div className="bg-white border border-emerald-200 rounded-lg p-2.5 shadow-sm">
                      <div className="text-emerald-700 font-semibold text-[11px] uppercase">Total Earnings</div>
                      <div className="text-emerald-900 font-bold text-lg font-mono">{inr(checklist.totals.total_earnings)}</div>
                    </div>
                    <div className="bg-white border border-rose-200 rounded-lg p-2.5 shadow-sm">
                      <div className="text-rose-700 font-semibold text-[11px] uppercase">Total Deductions</div>
                      <div className="text-rose-900 font-bold text-lg font-mono">{inr(checklist.totals.total_deductions)}</div>
                    </div>
                    <div className={`bg-white border rounded-lg p-2.5 shadow-sm ${
                      checklist.totals.net_settlement >= 0 ? 'border-blue-300' : 'border-amber-300'}`}>
                      <div className={`font-semibold text-[11px] uppercase ${
                        checklist.totals.net_settlement >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
                        Net Settlement
                      </div>
                      <div className={`font-bold text-lg font-mono ${
                        checklist.totals.net_settlement >= 0 ? 'text-blue-900' : 'text-amber-900'}`}>
                        {inr(checklist.totals.net_settlement)}
                      </div>
                    </div>
                    <div className="bg-white border border-slate-300 rounded-lg p-2.5 shadow-sm">
                      <div className="text-slate-500 font-semibold text-[11px] uppercase">Payment Mode</div>
                      <div className="text-slate-900 font-bold text-sm mt-1">{form.payment_mode}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 pt-1">
                    <button onClick={handleCompute} disabled={computing || !form.employee_id}
                      className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 font-medium">
                      <RefreshCw className="w-3.5 h-3.5" /> Re-compute Auto Engine
                    </button>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setShowModal(false)}
                        className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-100 font-medium">
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={!checklist || (checklist?.asset_clearance && !checklist.asset_clearance.cleared)}
                        title={checklist?.asset_clearance && !checklist.asset_clearance.cleared ? 'Clear pending assets first' : 'Save Settlement Statement'}
                        data-testid="fnf-save-btn"
                        className="flex items-center gap-1.5 px-5 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed">
                        <Save className="w-4 h-4" /> Save Settlement Statement
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add Custom Component Modal */}
        {showAddComponentModal && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowAddComponentModal(false)}>
            <div className="bg-white rounded-xl w-full max-w-md shadow-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b pb-3">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-blue-600" /> Add Custom Component
                </h3>
                <button onClick={() => setShowAddComponentModal(false)}><X className="w-4 h-4 text-slate-400" /></button>
              </div>

              <div className="space-y-3 text-xs">
                <div>
                  <label className="block font-medium text-slate-700 mb-1">Select from Salary Head Master (Optional)</label>
                  <select
                    value={newComponentForm.head_code}
                    onChange={(e) => {
                      const code = e.target.value;
                      const sh = salaryHeads.find(s => s.code === code);
                      if (sh) {
                        setNewComponentForm({
                          ...newComponentForm,
                          head_code: sh.code,
                          label: sh.name || sh.label || sh.code,
                          category: (sh.type || sh.category || 'earning').toLowerCase().includes('deduct') ? 'deduction' : 'earning',
                        });
                      } else {
                        setNewComponentForm({ ...newComponentForm, head_code: code });
                      }
                    }}
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 bg-white"
                  >
                    <option value="">Select Salary Head Master…</option>
                    {salaryHeads.map(sh => (
                      <option key={sh.id || sh._id || sh.code} value={sh.code}>
                        {sh.name || sh.label} ({sh.code})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-medium text-slate-700 mb-1">Component Label *</label>
                  <input
                    type="text"
                    placeholder="e.g. Retention Bonus, Equipment Allowance..."
                    value={newComponentForm.label}
                    onChange={(e) => setNewComponentForm({ ...newComponentForm, label: e.target.value })}
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 bg-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block font-medium text-slate-700 mb-1">Category</label>
                    <select
                      value={newComponentForm.category}
                      onChange={(e) => setNewComponentForm({ ...newComponentForm, category: e.target.value })}
                      className="w-full border border-slate-300 rounded px-2.5 py-1.5 bg-white"
                    >
                      <option value="earning">+ Earning</option>
                      <option value="deduction">− Deduction</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-medium text-slate-700 mb-1">Amount (₹) *</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={newComponentForm.amount}
                      onChange={(e) => setNewComponentForm({ ...newComponentForm, amount: e.target.value })}
                      className="w-full border border-slate-300 rounded px-2.5 py-1.5 bg-white font-mono font-bold"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-slate-700 mb-1">Formula / Calculation Note</label>
                  <input
                    type="text"
                    placeholder="e.g. Approved by HR Director..."
                    value={newComponentForm.formula}
                    onChange={(e) => setNewComponentForm({ ...newComponentForm, formula: e.target.value })}
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 bg-white"
                  />
                </div>

                <div>
                  <label className="block font-medium text-slate-700 mb-1">Remarks</label>
                  <input
                    type="text"
                    placeholder="HR Remarks..."
                    value={newComponentForm.remarks}
                    onChange={(e) => setNewComponentForm({ ...newComponentForm, remarks: e.target.value })}
                    className="w-full border border-slate-300 rounded px-2.5 py-1.5 bg-white"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 border-t pt-3">
                <button
                  onClick={() => setShowAddComponentModal(false)}
                  className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded text-xs font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCustomComponent}
                  className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-bold"
                >
                  Add Component
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
